/**
 * Pure helpers for the player's quality menu — kept out of the hook so the
 * labelling and ordering rules are testable without a DOM or a media element.
 */

/** Sentinel for "let the player choose" — matches hls.js' own -1 convention. */
export const AUTO_QUALITY_INDEX = -1;

export interface ClipQualityLevel {
  /** Index into the manifest's level list; what gets handed back to hls.js. */
  readonly index: number;
  /** Frame height, 0 when the manifest omits it. */
  readonly height: number;
  readonly bitrate: number;
  /** Menu text, e.g. "1080p". */
  readonly label: string;
}

export interface ClipQualityState {
  /** Highest first. Empty for a plain MP4 source — the menu then stays hidden. */
  readonly levels: readonly ClipQualityLevel[];
  /** What the viewer picked; {@link AUTO_QUALITY_INDEX} means automatic. */
  readonly selectedIndex: number;
  /** What is actually playing right now, so "Auto" can show it. */
  readonly activeIndex: number;
  readonly setSelectedIndex: (index: number) => void;
}

export const EMPTY_CLIP_QUALITY_STATE: ClipQualityState = {
  levels: [],
  selectedIndex: AUTO_QUALITY_INDEX,
  activeIndex: AUTO_QUALITY_INDEX,
  setSelectedIndex: () => {},
};

/**
 * A manifest may omit RESOLUTION, so fall back to the bitrate rather than
 * showing an unlabelled entry the viewer cannot tell apart from its neighbour.
 */
export function formatQualityLabel(level: {
  readonly height: number;
  readonly bitrate: number;
}): string {
  if (level.height > 0) return `${level.height}p`;
  const mbit = level.bitrate / 1_000_000;
  return `${mbit.toFixed(1).replace(".", ",")} Mbit/s`;
}

/**
 * Manifest levels → menu entries, best first (that is the order viewers expect,
 * and it puts the quality they most likely want at the top).
 *
 * `index` deliberately survives the sort: hls.js addresses levels by their
 * position in the manifest, so the menu must hand back the original index, not
 * the display position.
 */
export function toQualityLevels(
  raw: readonly { readonly height?: number; readonly bitrate?: number }[],
): readonly ClipQualityLevel[] {
  return raw
    .map((level, index) => {
      const height = level.height ?? 0;
      const bitrate = level.bitrate ?? 0;
      return {
        index,
        height,
        bitrate,
        label: formatQualityLabel({ height, bitrate }),
      };
    })
    .sort((a, b) => b.height - a.height || b.bitrate - a.bitrate);
}

/**
 * True when the source is an HLS playlist rather than a plain file.
 *
 * Matches on the path only — a signed CloudFront URL carries its policy in the
 * query string, and an `.m3u8?Key-Pair-Id=…` must still be recognised.
 */
export function isHlsSource(src: string): boolean {
  const path = src.split("?")[0] ?? "";
  return path.toLowerCase().endsWith(".m3u8");
}
